import { NotificationTypes, type SafeAny } from '@/common/types';
import { axiosInstance } from '@/common/utils';
import type { ScoreSetEventsData } from '@/modules/epoch/types';
import type { SignalEvent } from '@/modules/epoch/data-access';
import { getData, HYPNOGRAM_EVENTS } from '@/modules/study/common';

const mapEventsBySignal = (
  events: ScoreSetEventsData,
  signalEventsMapping: { [signal: string]: string[] }
): {
  [signal: string]: SignalEvent[];
} =>
  Object.keys(signalEventsMapping).reduce(
    (acc, signal: string) => ({
      ...acc,
      [signal]: signalEventsMapping[signal].reduce(
        (signalEvents, eventType: string) => [
          ...signalEvents,
          ...(events[eventType]?.map((item) => ({
            ...item,
            eventType
          })) ?? [])
        ],
        [] as SignalEvent[]
      )
    }),
    {}
  );

export const getStudyScoreSetEvents = async (
  { rootState, state = { events: {} }, commit }: SafeAny,
  {
    studyId,
    epochSize = 30
  }: {
    studyId: string;
    epochSize?: number;
  }
) => {
  const url = `${rootState.env?.requestUrl.events}/studies/${studyId}/events`;

  state.events.isLoading = true;
  rootState.study.hypnogram.isLoading = true;
  state.events.error = undefined;

  try {
    const { data } = await axiosInstance.get<ScoreSetEventsData>(url, {
      params: {
        epochSize
      }
    });
    state.events.data = data;
    state.events.isLoading = false;

    const signalEvents = mapEventsBySignal(data, state.signalEventMapping);

    const hypnogramEvents = Object.fromEntries(
      Object.entries(rootState.epoch.events.data).filter(([key]) => HYPNOGRAM_EVENTS.includes(key))
    );

    const newHypData = getData(
      rootState.scoreSet.scoreSet.data,
      hypnogramEvents as ScoreSetEventsData
    );

    commit('study/setHypnogramData', { ...newHypData }, { root: true });
    rootState.study.hypnogram.isLoading = false;

    commit('setSignalEvents', signalEvents);
  } catch (error: SafeAny) {
    state.events.isLoading = false;
    rootState.study.hypnogram.isLoading = false;
    state.events.data = [];

    commit(
      'addNotification',
      {
        i18nTitle: 'errorGetScoreSetEvents',
        i18nDescription: error.message,
        type: NotificationTypes.Error
      },
      { root: true }
    );
  }
};
